"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import styles from "../css/invoice.module.css";
import { getService, listServices } from "../../../../api/clinic/servicesApi.js";
import {
  createClinicCreditTransaction,
  updateAppointmentStatus,
} from "../../../../api/clinic/consultationApi.js";
import { isTenStepConsultService, getRouteForService } from "../components/ServiceRouting.js";
import { createConsultationIfNeeded } from "../components/ConsultationCreator.jsx";
import { scheduleFollowUpIfNeeded } from "../components/FollowUpScheduler.jsx";
import { processPrescription } from "../components/PrescriptionProcessor.jsx";
import { updatePatientProfile } from "../components/PatientUpdater.jsx";
import { saveAllDrafts } from "../utils/consultationDraftSaver.js";
import FinalizeConfirmationModal from "../components/FinalizeConfirmationModal.jsx";
import ServiceSelection from "../components/ServiceSelection.jsx";
import LineItems from "../components/LineItems.jsx";
import InvoiceHeader from "../components/InvoiceHeader.jsx";

// Helper functions to extract service information from workflow data
const getServiceNameFromKey = (serviceKey) => {
  const serviceNames = {
    hiv_test: 'HIV Test',
    baby_immunization: 'Baby Immunization',
    wound_care: 'Wound Care',
    diet_plus_injection: 'Diet Plus Injection',
    metabolic_consult: 'Metabolic Consult',
    urine_dipstick: 'Urine Dipstick Test',
    peak_flow: 'Peak Flow Test',
    pap_smear: 'Pap Smear',
    nebulisation: 'Nebulisation',
    lipogram_test: 'Lipogram Test',
    injection: 'Injection',
    hemoglobin_test: 'Hemoglobin Test',
    health_risk_assessment: 'Health Risk Assessment',
    family_planning: 'Family Planning',
    glucose_test: 'Glucose Test',
    cholesterol_test: 'Cholesterol Test',
    diet_consult_weekly: 'Diet Consult (Weekly)',
    ear_syringing: 'Ear Syringing',
    diabetes_consult: 'Diabetes Consult',
    blood_pressure: 'Blood Pressure',
  };
  return serviceNames[serviceKey] || 'Service';
};

const getServiceCodeFromData = (serviceData) => {
  // Try to extract service code from service data
  if (serviceData.servicecode) return serviceData.servicecode;
  if (serviceData.serviceCode) return serviceData.serviceCode;
  return '';
};

export default function Invoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, stepDrafts, getStepDraft, setStepDraft, updateConsultationData, endConsultation, clearStepDrafts } = useConsultation();

  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState("");
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);

  const draftKey = "invoice";
  const defaultDraft = {
    selectedService: "",
    lineItems: [],
    pendingWorkflowServiceId: null,
  };

  const draft = getStepDraft(draftKey, defaultDraft);
  const [selectedService, setSelectedService] = useState(draft.selectedService || "");

  const pendingWorkflowServiceId = draft?.pendingWorkflowServiceId ?? null;

  const [serviceOptions, setServiceOptions] = useState([]);
  const [lineItems, setLineItems] = useState(Array.isArray(draft.lineItems) ? draft.lineItems : defaultDraft.lineItems);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const services = await listServices();
        if (cancelled) return;
        setServiceOptions(Array.isArray(services) ? services : []);
      } catch {
        if (cancelled) return;
        setServiceOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const st = location?.state || {};
    const returnedFromWorkflow = !!st.returnedFromWorkflow || !!st.returnToInvoice;
    const returnedServiceId =
      st.addedServiceId ??
      st.serviceId ??
      st.serviceid ??
      consultationData?.activeWorkflowServiceId ??
      null;

    if (!returnedFromWorkflow) return;

    if (!pendingWorkflowServiceId) {
      if (consultationData?.activeWorkflowServiceId) updateConsultationData({ activeWorkflowServiceId: null });
      return;
    }

    if (!returnedServiceId || String(pendingWorkflowServiceId || '') === String(returnedServiceId)) {
      setStepDraft(draftKey, (prev) => ({
        ...(prev || {}),
        pendingWorkflowServiceId: null,
      }));
      updateConsultationData({ activeWorkflowServiceId: null });
    }
  }, [location?.state, pendingWorkflowServiceId, consultationData?.activeWorkflowServiceId]);

  useEffect(() => {
    let cancelled = false;
    const serviceId = consultationData?.baseServiceId || consultationData?.serviceId;
    if (!serviceId) return;

    (async () => {
      try {
        const svc = await getService(serviceId);
        if (cancelled) return;

        if (svc?.servicename && !consultationData?.serviceName) {
          updateConsultationData({ serviceName: svc.servicename });
        }
        if (svc?.servicecode && !consultationData?.serviceCode) {
          updateConsultationData({ serviceCode: svc.servicecode });
        }

        const hasMeaningfulItems = Array.isArray(lineItems) && lineItems.length > 0;
        const hasDraftedInvoice = hasMeaningfulItems;
        if (hasDraftedInvoice) return;

        const unitPrice = Number(svc?.price) || 0;
        const next = [
          {
            id: Date.now(),
            code: svc?.servicecode || "",
            description: svc?.servicename || "Service",
            quantity: 1,
            unitPrice,
            total: unitPrice,
          },
        ];
        setLineItems(next);
        persistDraft("", next);
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [consultationData?.baseServiceId, consultationData?.serviceId]);

  const persistDraft = (nextSelectedService, nextLineItems) => {
    setStepDraft(draftKey, {
      ...(draft || {}),
      selectedService: nextSelectedService,
      lineItems: nextLineItems,
    });
  };

  const handleAddService = () => {
    const service = (serviceOptions || []).find((s) => String(s?.serviceid) === String(selectedService));

    if (!service) return;

    const baseServiceId = consultationData?.baseServiceId || consultationData?.serviceId || null;
    if (!consultationData?.baseServiceId && baseServiceId) {
      updateConsultationData({ baseServiceId });
    }

    const nextConsultationData = { activeWorkflowServiceId: service?.serviceid || null };
    if (isTenStepConsultService(service)) {
      nextConsultationData.serviceId = service?.serviceid;
      // Reset returnedFromWorkflow flag for normal 10-step consultations
      // This prevents PCDT flow from persisting when user adds general consultations
      updateConsultationData({ ...nextConsultationData, returnedFromWorkflow: false });
    }
    updateConsultationData(nextConsultationData);

    const newItem = {
      id: Date.now(),
      serviceid: service?.serviceid,
      code: service.servicecode || "",
      description: service.servicename || "Service",
      quantity: 1,
      unitPrice: Number(service.price) || 0,
      total: Number(service.price) || 0,
    };

    const next = [...lineItems, newItem];
    setLineItems(next);
    setStepDraft(draftKey, (prev) => ({
      ...(prev || {}),
      selectedService: "",
      lineItems: next,
      pendingWorkflowServiceId: service?.serviceid || null,
    }));

    setSelectedService("");

    const { route, continueTo } = getRouteForService(service);
    navigate(route, {
      state: {
        ...(location?.state || {}),
        returnToInvoice: true,
        addedServiceId: service?.serviceid,
        ...(continueTo ? { continueTo } : {}),
      },
    });
  };

  const handleDelete = (id) => {
    const next = lineItems.filter((item) => item.id !== id);
    setLineItems(next);
    persistDraft(selectedService, next);
  };

  const handleChange = (id, field, value) => {
    setLineItems((prev) => {
      const next = prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          updated.total = updated.quantity * updated.unitPrice;
          return updated;
        }
        return item;
      });
      persistDraft(selectedService, next);
      return next;
    });
  };

  const handleSelectedServiceChange = (val) => {
    setSelectedService(val);
    persistDraft(val, lineItems);
  };

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      setFinalizeError("");

      const consultationId = await createConsultationIfNeeded(consultationData, lineItems, updateConsultationData);
      const drafts = stepDrafts || {};
      const reviewDraft = getStepDraft?.("reviewFollowUp", null);

      // Collect all services for the clinic script
      // 1. Services from workflow data (e.g., HIV service)
      const workflowServices = [];
      const serviceWorkflowData = drafts.serviceWorkflowData || {};
      for (const [serviceId, serviceData] of Object.entries(serviceWorkflowData)) {
        // Extract service info from workflow data
        const serviceKey = Object.keys(serviceData)[0]; // e.g., 'hiv_test', 'general_consult'
        if (serviceKey && serviceData[serviceKey]) {
          workflowServices.push({
            serviceid: serviceId,
            servicename: getServiceNameFromKey(serviceKey),
            servicecode: getServiceCodeFromData(serviceData[serviceKey]),
            price: 0, // Will be updated from actual service data
            quantity: 1,
          });
        }
      }

      // 2. Services from current line items (e.g., general consult)
      const lineItemServices = lineItems.map(item => ({
        serviceid: item.serviceid,
        servicename: item.description,
        servicecode: item.code,
        price: item.unitPrice,
        quantity: item.quantity,
      }));

      // 3. Combine all services
      const allServices = [...workflowServices, ...lineItemServices];

      // Schedule follow-up if needed
      if (reviewDraft?.followUpDate) {
        try {
          await scheduleFollowUpIfNeeded(consultationData, reviewDraft);
        } catch (e) {
          setFinalizeError(e.message);
          return;
        }
      }

      // Save all consultation drafts
      await saveAllDrafts(consultationId, drafts);

      // Process prescription with all services
      await processPrescription(consultationData, consultationId, drafts.prescription, allServices);

      // Create credit transaction
      const patientId = consultationData?.patientId;
      const invoiceTotal = lineItems.reduce((sum, item) => sum + Number(item?.total || 0), 0);
      if (patientId && Number.isFinite(invoiceTotal) && invoiceTotal > 0) {
        await createClinicCreditTransaction({
          patientid: patientId,
          transactionamount: +invoiceTotal.toFixed(2),
          type: 'debit',
          details: `CONS-${String(consultationId)}`,
        });
      }

      // Update appointment status
      const appointmentId = consultationData?.appointmentId;
      if (appointmentId) {
        await updateAppointmentStatus(appointmentId, "Completed");
      }

      // Update patient profile
      try {
        await updatePatientProfile(consultationData, getStepDraft);
      } catch (e) {
        // Patient profile update failure shouldn't stop the process
        console.error("Patient profile update failed:", e);
      }

      clearStepDrafts();
      endConsultation();
      navigate("/customer/clinic/consultations");
    } catch (e) {
      setFinalizeError(e?.response?.data?.message || e?.message || "Failed to finalize consultation");
    } finally {
      setFinalizing(false);
    }
  };

  const handleFinalizeClick = () => {
    if (finalizing || !!pendingWorkflowServiceId) return;
    setShowFinalizeConfirm(true);
  };

  const handleConfirmFinalize = () => {
    if (finalizing || !!pendingWorkflowServiceId) return;
    setShowFinalizeConfirm(false);
    handleFinalize();
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + item.total, 0);
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          {/* Header */}
          <InvoiceHeader consultationData={consultationData} />

          {/* Add Services Card */}
          <ServiceSelection
            serviceOptions={serviceOptions}
            selectedService={selectedService}
            onServiceChange={handleSelectedServiceChange}
            onAddService={handleAddService}
          />

          {/* Line Items Card */}
          <LineItems
            lineItems={lineItems}
            onItemChange={handleChange}
            onDeleteItem={handleDelete}
            calculateTotal={calculateTotal}
          />

          {!!finalizeError && (
            <div className={styles.card}>
              <div style={{ color: "#b91c1c" }}>{finalizeError}</div>
            </div>
          )}

          {/* Finalize */}
          <div className={styles.finalizeWrapper}>
            <button className={styles.finalizeBtn} onClick={handleFinalizeClick} disabled={finalizing || !!pendingWorkflowServiceId}>
              Finalize Invoice →
            </button>
          </div>

          <FinalizeConfirmationModal
            show={showFinalizeConfirm}
            onCancel={() => setShowFinalizeConfirm(false)}
            onConfirm={handleConfirmFinalize}
            disabled={finalizing || !!pendingWorkflowServiceId}
          />

        </div>
      </main>

      <Footer />
    </div>
  );
}